import { ReactNode } from "react";

import { InfoCircle } from "components/icons/generated";
import SearchInput from "components/SearchInput";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import Badge from "ds/components/Badge";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ButtonIcon from "ds/components/ButtonIcon";
import Tab from "ds/components/Tab";
import Tooltip from "ds/components/Tooltip";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import NonRootApiKeysToggle from "components/ApiKeys/NonRootApiKeysToggle";
import ApiKeyManagementFromNonHumansToggle from "components/ApiKeys/ApiKeyManagementFromNonHumansToggle";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import useCanManageApiKeys from "shared/ApiKey/access/useCanManageApiKeys";
import useCanViewUsersPersonalApiKeys from "shared/ApiKey/access/useCanViewUsersPersonalApiKeys";

import { SettingsContext } from "../Context";
import { getManagementStrategy } from "../helpers";
import SeatLimitsNotification from "../SeatLimitsNotification";
import { SeatLimitsNotificationEntity } from "../SeatLimitsNotification/types";
import { UserManagementActivationStatus } from "../types";
import UserManagementWarningCallout from "../UserManagementWarningCallout";
import { showApiKeysInfoDrawer } from "./InfoDrawer";
import { API_KEYS_FILTERS_ORDER_SETTINGS_KEY } from "./List/constants";
import useApiKeysCreateAccess from "./useCreateAccess";
import PersonalApiKeysInfoCallout from "./PersonalApiKeysInfoCallout";

type ApiKeysPageLayoutProps = {
  children: ReactNode;
  openCreateApiKey?: () => void;
  hasApiKeys?: boolean;
  showPersonalApiKeysCallout?: boolean;
};

const ApiKeysPageLayout = ({
  children,
  openCreateApiKey,
  hasApiKeys = false,
  showPersonalApiKeysCallout = false,
}: ApiKeysPageLayoutProps) => {
  const { enableApiKeyCreationForNonRootAdminsFrontend, personalApiKeys } = useTypedFlags();
  const { seats, activationStatus } = useTypedContext(SettingsContext);
  const canManageApiKeys = useCanManageApiKeys();
  const canViewUsersPersonalApiKeys = useCanViewUsersPersonalApiKeys();

  const { canCreateApiKeys, isCreateDisabled, createDisabledTooltip } = useApiKeysCreateAccess();

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationApiKeys,
    defaultCallbackTrackProperties: { managementStrategy: getManagementStrategy(activationStatus) },
  });

  const openInfoDrawer = () => {
    trackSegmentAnalyticsEvent("Info Click");
    showApiKeysInfoDrawer();
  };

  const titleAndInfo = (
    <Box direction="row" align="center" gap="large">
      <PageInfoHeaderTitle title="API keys" />
      <ButtonIcon onPress={openInfoDrawer} icon={InfoCircle} variant="ghost">
        Open info drawer
      </ButtonIcon>
    </Box>
  );

  const listActions = (hasApiKeys || (canCreateApiKeys && openCreateApiKey)) && (
    <PageInfoHeaderActions>
      {hasApiKeys && (
        <SearchInput
          placeholder={SEARCH_PLACEHOLDERS.API_KEYS}
          filtersOrderSettingsKey={API_KEYS_FILTERS_ORDER_SETTINGS_KEY}
        />
      )}
      {canCreateApiKeys && openCreateApiKey && (
        <Tooltip
          active={isCreateDisabled}
          on={(props) => (
            <Button
              onPress={openCreateApiKey}
              variant="primary"
              disabled={isCreateDisabled}
              {...props}
            >
              Create API key
            </Button>
          )}
        >
          {createDisabledTooltip}
        </Tooltip>
      )}
    </PageInfoHeaderActions>
  );

  return (
    <>
      {activationStatus === UserManagementActivationStatus.ACTIVE && (
        <SeatLimitsNotification
          entity={SeatLimitsNotificationEntity.ApiKey}
          seats={seats.apiKey}
          type="callout"
        />
      )}
      <UserManagementWarningCallout analyticsPage={AnalyticsPageOrganization.OrganizationApiKeys} />
      {personalApiKeys && (
        <>
          <PageInfoHeaderWrapper align="start">
            <Box direction="column">
              {titleAndInfo}
              <ViewHeaderNavigation>
                <Tab to="/settings/api-keys" end label="Organization" />
                {canViewUsersPersonalApiKeys && (
                  <Tab
                    to="/settings/api-keys/personal"
                    label="Personal"
                    suffixComponent={<Badge variant="blue" type="strong" text="BETA" />}
                  />
                )}
                <Tab to="/settings/api-keys/settings" label="Settings" />
              </ViewHeaderNavigation>
            </Box>
            {listActions}
          </PageInfoHeaderWrapper>
          {showPersonalApiKeysCallout && <PersonalApiKeysInfoCallout />}
        </>
      )}
      {!personalApiKeys && (
        <PageInfoHeaderWrapper>
          <Box direction="column" fullWidth>
            <Box fullWidth justify="between">
              {titleAndInfo}
              {listActions}
            </Box>
            {canManageApiKeys && (
              <Box margin="large 0 small" direction="row">
                {enableApiKeyCreationForNonRootAdminsFrontend && <NonRootApiKeysToggle />}
                <ApiKeyManagementFromNonHumansToggle />
              </Box>
            )}
          </Box>
        </PageInfoHeaderWrapper>
      )}
      {children}
    </>
  );
};

export default ApiKeysPageLayout;
